import { useState, useEffect, type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import { buildConfirmModalHandlers } from "./confirmModalHandlers";
import { buildConfirmModalDerivedProps } from "./confirmModalButtonPropsHelpers";
import type { ConfirmModalStateParams } from "./useConfirmModalStateParams";

export type ConfirmModalStateReturn = {
	confirmationValue: string;
	setConfirmationValue: Dispatch<SetStateAction<string>>;
	handleClose: () => void;
	confirmButtonProps: ReturnType<typeof buildConfirmModalDerivedProps>["confirmButtonProps"];
	cancelButtonProps: ReturnType<typeof buildConfirmModalDerivedProps>["cancelButtonProps"];
	thirdButtonProps: ReturnType<typeof buildConfirmModalDerivedProps>["thirdButtonProps"];
	onConfirmationChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
};

export function useConfirmModalStateCore(props: ConfirmModalStateParams): ConfirmModalStateReturn {
	const [confirmationValue, setConfirmationValue] = useState("");
	useEffect(() => {
		if (props.isOpen) setConfirmationValue("");
	}, [props.isOpen]);
	const { handleConfirm, handleCancel, handleClose } = buildConfirmModalHandlers({
		requireConfirmation: props.requireConfirmation,
		confirmationValue,
		setConfirmationValue,
		confirmButton: props.confirmButton,
		onConfirm: props.onConfirm,
		cancelButton: props.cancelButton,
		onCancel: props.onCancel,
		onClose: props.onClose,
		closeOnConfirmStart: props.closeOnConfirmStart,
	});
	const { confirmButtonProps, cancelButtonProps, thirdButtonProps } = buildConfirmModalDerivedProps({
		variant: props.variant,
		requireConfirmation: props.requireConfirmation,
		confirmationValue,
		loading: props.loading,
		confirmButton: props.confirmButton,
		onConfirm: props.onConfirm,
		handleConfirm,
		cancelButton: props.cancelButton,
		handleCancel,
		thirdButton: props.thirdButton,
	});
	const onConfirmationChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void =>
		setConfirmationValue(event.target.value);
	return { confirmationValue, setConfirmationValue, handleClose, confirmButtonProps, cancelButtonProps, thirdButtonProps, onConfirmationChange };
}
